import { faHandshake, faPlaneDeparture, faSignature } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useEffect, useState } from 'react'
import Confetti from 'react-confetti'
import { useNavigate, useParams } from 'react-router'
import useWindowSize from 'react-use/lib/useWindowSize'

import { type OnboardingPage } from '../routes'
import useMe from '../../utils/useMe'
import { OnboardingTitle } from '../components/OnboardingTitle'
import { track } from '../../utils/analytics'

import { Button, Card, CardActions, CardContent } from '@olino/design-system'
import { useQuotation } from '../context'
import {
  DealCreateInput,
  ProductEnum,
  useCreateDealActiveCampaignQuery,
} from '../../generated/graphql'
import { useOnboardingWorkflow } from '../workflow'

const Main = () => {
  const { id } = useParams()
  const { width, height } = useWindowSize()
  const user = useMe()
  const navigate = useNavigate()
  const { quotation, isUpdating, updateQuotation } = useQuotation()
  const { proceed } = useOnboardingWorkflow()

  const [hasUserRead, setHasUserRead] = useState(false)
  const [isUpdated, setIsUpdated] = useState(false)

  // Adding a timer to make sure the user has read the message
  useEffect(() => {
    setTimeout(() => {
      setHasUserRead(true)
    }, 2000)
  }, [])

  useEffect(() => {
    track({
      event: 'visited_success_page',
      quotation_id: id,
    })
  }, [id])

  let quotChem
  if (quotation.details.products?.includes(ProductEnum.MreAcheel)) {
    quotChem = quotation.premium?.mreAcheel
  }

  const deal: DealCreateInput = {
    quotation: {
      number: quotation.number,
      listName: 'OC - Professionnels Eco',
      pipelineTitle: 'Pro Eco',
      id: quotation.id,
      originalTotal: quotChem?.originalTotal ? quotChem.originalTotal : 0,
      discountedTotal: quotChem?.discountedTotal,
      base: quotChem?.base,
      status: quotation.status,
      quotation: {
        activeCampaignId: quotation.details.activeCampaignId,
        activeCampaignDealId: quotation.details.activeCampaignDealId,
        startDate: quotation.details.startDate,
        contact: {
          email: quotation.details.contact?.email,
          phone: quotation.details.contact?.phone,
          firstName: quotation.details.contact?.firstName,
          lastName: quotation.details.contact?.lastName,
          marketingOptIn: quotation.details.contact?.marketingOptIn,
          userTypeName: quotation.details.contact?.userTypeName,
        },
        products: quotation.details.products,
        turnover: quotation.details.turnover,
        billingAddress: {
          city: quotation.details.billingAddress?.city,
          line1: quotation.details.billingAddress?.line1,
          line2: quotation.details.billingAddress?.line2,
          country: quotation.details.billingAddress?.country,
          postalCode: quotation.details.billingAddress?.postalCode,
        },
        company: {
          name: quotation.details.company?.name,
          nafCode: quotation.details.company?.nafCode,
          siret: quotation.details.company?.siret,
          legalStatus: quotation.details.company?.legalStatus,
          foundedAt: quotation.details.company?.foundedAt,
          address: {
            city: quotation.details.company?.address?.city,
            line1: quotation.details.company?.address?.line1,
            line2: quotation.details.company?.address?.line2,
            postalCode: quotation.details.company?.address?.postalCode,
            country: quotation.details.company?.address?.country,
          },
        },
        mreAcheel: {
          activityId: quotation.details.mreAcheel?.activityId,
          packageOptionMAcheel: quotation.details.mreAcheel?.packageOptionMAcheel,
          surfaceArea: quotation.details.mreAcheel?.surfaceArea,
          // assistanceOption: quotation.details.mreAcheel?.assistanceOption,
          nbSinistres: quotation.details.mreAcheel?.nbSinistres,
          deductibleAmount: quotation.details.mreAcheel?.deductibleAmount,
          extincteursLocaux: quotation.details.mreAcheel?.extincteursLocaux,
          historicalMonument: quotation.details.mreAcheel?.historicalMonument,
          resilieNonPaiement: quotation.details.mreAcheel?.resilieNonPaiement,
          procedureCollective: quotation.details.mreAcheel?.procedureCollective,
          installElectriqueVerif: quotation.details.mreAcheel?.installElectriqueVerif,

          // assistanceOption: quotation.details.mreAcheel?.assistanceOption,
          // legalProtectionOption: quotation.details.mreAcheel?.legalProtectionOption,
          // operatingLiabilityOption: quotation.details.mreAcheel?.operatingLiabilityOption,
          // financialLossStandardOption: quotation.details.mreAcheel?.financialLossStandardOption,
          // financialLossAdvantageOption: quotation.details.mreAcheel?.financialLossAdvantageOption,
        },
        paymentFrequency: quotation?.details.paymentFrequency,
      },
    },
  }

  if (
    quotation.details.products?.includes(ProductEnum.MreAcheel) &&
    !isUpdated &&
    quotation.details.mreAcheel?.procedureCollective == false
  ) {
    const { data: results, isLoading } = useCreateDealActiveCampaignQuery({ query: deal })
    useEffect(() => {
      if (!isLoading) {
        updateQuotation({
          details: {
            activeCampaignId: results?.createDealActiveCampaign?.activeCampaignId,
            activeCampaignDealId: results?.createDealActiveCampaign?.activeCampaignDealId,
          },
        })
        setIsUpdated(true)
      }
    }, [])
  }

  useEffect(() => {
    // Clearing the quotation from the local storage to avoid the user going back to a quotation that's already completed
    localStorage.removeItem('lastQuotationId')
  }, [])

  return (
    <>
      <Confetti width={width} height={height} recycle={false} className="fixed" />

      <OnboardingTitle.H1>
        <span className="capitalize">{user?.firstName}</span>, bienvenue chez Pilliot Assurances !
      </OnboardingTitle.H1>

      <div className="mb-5 font-medium">
        Votre souscription a bien été enregistrée, voici les prochaines étapes pour rendre votre
        contrat définitif :
      </div>

      <Card className="sm:pt-4">
        <CardContent>
          <div className="grid max-w-4xl grid-cols-1 space-y-4 text-center text-sm sm:gap-6 md:grid-cols-2 md:flex-row md:space-y-0">
            <div>
              <h2 className="font-serif mb-3 text-xl font-black">
                Récupérez votre note de couverture provisoire
              </h2>
              <p>
                Vous pourrez récupérer votre note de couverture provisoire signée dès la date
                d'effet de votre contrat.
                {/* <span className="font-bold">
                  Dès la signature, vous pourrez retrouver votre attestation et l'ensemble de vos
                  documents sur le Cockpit.
                </span> */}
              </p>
              <br />
              <p>
                Vous recevrez votre attestation définitive lorsque tous vos documents seront
                validés.
              </p>

              <div className="mt-6 flex flex-col items-center justify-center gap-6 sm:flex-row">
                <Button disabled={!hasUserRead} variant="outlined" onClick={() => navigate('/')}>
                  Télécharger ma note de couverture
                </Button>
                {/*<Button onClick={() => navigate('/onboarding')}>Nouveau devis</Button>*/}
              </div>
            </div>
            <div>
              <h2 className="font-serif mb-3 text-xl font-black">
                Déposez vos documents obligatoires
              </h2>
              <p>Pour que votre contrat soit validé, nous avons besoin de certains documents.</p>
              <br />
              <p>Merci de les déposer sur notre plateforme en cliquant ci-dessous:</p>
              <div className="mt-6 flex flex-col items-center justify-center gap-6 sm:flex-row">
                <Button
                  onClick={() =>
                    (window.location.href = `https://3qlbkt67o9c.typeform.com/to/QbGFTXpo?utm_source=tarificateur&utm_medium=btn#email=${user?.email}&first_name=${user?.firstName}`)
                  }
                >
                  Déposer mes documents
                </Button>
              </div>
            </div>
          </div>
        </CardContent>

        <CardActions></CardActions>
      </Card>
    </>
  )
}

const Tips = () => {
  return undefined
}

export default {
  main: <Main />,
  tips: <Tips />,
} satisfies OnboardingPage
